import {css} from '@emotion/react'
import {CustomFieldMembersComponentProps} from 'module/CustomField/type/CFComponentType'
import {CFMember, CFValue} from 'module/CustomField/type/CFType'
import React, {useCallback, useMemo} from 'react'
import {CF} from 'module/CustomField/util/CustomField'
import {CustomFieldComponent} from 'module/CustomField/component/CustomFieldComponent'
import {BSAttr} from 'module/BSEntry/BSAttr'
import {SisName} from 'util/SisName'
import {SetCb} from 'type/SetCb'
import {AttachmentFetchMode} from 'generated/JavaTO'
import {isEqualCFValue} from 'module/CustomField/util/isEqualCFValue'

const cssField = css`
	display: grid;
	grid-template-columns: auto min-content;
`

interface CustomFieldMemberComponentProps extends CustomFieldMembersComponentProps {
	cfMember: CFMember | CFValue
	isCFNamespace: boolean
}

const EMPTY = {}
export const CustomFieldMemberComponent = React.memo(function CustomFieldMemberComponent(props: CustomFieldMemberComponentProps) {
	const {
		cfMember, pos, isCFNamespace, cfDef, model, persistedModel, inlineLabel, labelHidden, labelDone, disabled,
		setCFValue, deleteCFValue, validateForm, fetchCFAttribute,
	} = props
	let posMember: number | undefined
	if (typeof pos === 'number') {
		posMember = pos
	} else {
		posMember = isCFNamespace && cfMember !== null ? (cfMember as CFMember).pos : undefined
	}
	const modelValue = CF.getValueFrom(cfDef, model, {pos: posMember, forView: true}) as CFMember | CFValue
	const rawValue = CF.getValueFrom(cfDef, model, {pos: posMember, forView: false}) as CFMember | CFValue
	const persistedValue = CF.getValueFrom(cfDef, persistedModel, {pos: posMember, forView: false}) as CFMember | CFValue
	const hasChanged = persistedValue && !isEqualCFValue(rawValue, persistedValue, cfDef) || undefined
	const setCb: SetCb<CFMember | CFValue> = useCallback((val) => {
		setCFValue({cfDef, value: val, options: {pos: posMember, userInput: true}})
		if (validateForm) validateForm({checkRemoteCollision: false})
	}, [setCFValue, validateForm, cfDef, posMember])
	const removeCb = useCallback(() => {
		deleteCFValue?.({cfDefs: [cfDef], pos: posMember});
		if (validateForm) validateForm({checkRemoteCollision: false})
	}, [cfDef, posMember, validateForm, deleteCFValue])
	const resetCb = useMemo(() => hasChanged && (() => {
		setCFValue({cfDef, value: CF.getValueFrom(cfDef, persistedModel, {pos: posMember}) as CFMember | CFValue})
		if (validateForm) validateForm({checkRemoteCollision: false})
	}), [cfDef, persistedModel, posMember, validateForm, setCFValue])
	const fetchCb = useCallback((fetchMode: AttachmentFetchMode) => {
		const id = BSAttr.getId(persistedModel)
		const sisName = persistedModel?.sisName as SisName.Booking | SisName.Persurl
		if (!fetchCFAttribute || typeof id !== 'number' || !sisName || typeof posMember !== 'number') return;
		fetchCFAttribute({
			sisName,
			id,
			dstField: CF.getCFNamespaceField(cfDef),
			pos: posMember,
			fetchMode,
		})
	}, [cfDef, persistedModel, posMember, fetchCFAttribute])
	return (
		<div
			css={cssField}
			className={'CustomFieldMember'}
			style={inlineLabel ? {display: 'inline-block'} : undefined}
			data-cfdefid={cfDef.id}
			data-cfdefpos={pos}
		>
			<CustomFieldComponent
				placeholderHTML={!labelDone && labelHidden ? cfDef.label : undefined}
				cfDef={cfDef}
				pos={posMember}
				model={model || EMPTY}
				modelValue={modelValue}
				rawValue={rawValue}
				setCb={setCb}
				removeCb={removeCb}
				resetCb={resetCb}
				fetchCb={fetchCb}
				disabled={disabled}
			/>
		</div>
	)
})